import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/Edit';
import {
  DataGridPremium,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarFilterButton,
  useGridApiRef,
  type GridCellParams,
  type GridColDef,
  type GridRenderEditCellParams,
  type GridRowId,
} from '@mui/x-data-grid-premium';
import { alpha, useTheme } from '@mui/material/styles';
import PageScaffold from '../../components/PageScaffold';
import { standardDataGridSx } from '../../components/dataGridStyles';
import { authFetch, getStoredUser } from '../../utils/auth';
import {
  appendQuickEntryUserToUrl,
  clearStoredQuickEntryUser,
  getStoredQuickEntryUser,
  quickEntryUserMatches,
} from '../../utils/quickEntryUser';

type LandingRecord = {
  id: string;
  sourceType: 'Patient' | 'Caregiver';
  name: string;
  mobile: string;
  phone: string;
  status: string;
  notes?: string;
  leadStage?: string;
  createdBy?: string;
  createdDate?: string;
  nyia1Date?: string;
  nyia1Status?: string;
  nyia2Date?: string;
  nyia2Status?: string;
  medicaidPayer?: string;
  medicaidPlanCode?: string;
  medcheckMedicaidStatus?: string;
  lineOfBusiness?: string;
  assessmentType?: string;
  startOfCareDate?: string;
  hardToReach?: boolean;
  email?: string;
  primaryLanguage?: string;
};

type GridLandingRow = LandingRecord & { uiId: string };

type EditableDisplayParams = {
  id: GridRowId;
  field: string;
  value?: unknown;
  colDef: {
    headerName?: string;
  };
};

type NyiaField = 'nyia1Date' | 'nyia1Status' | 'nyia2Date' | 'nyia2Status';

type ActivityRecord = {
  id: string;
  subject: string;
  status?: string;
  activityDate?: string;
  startDateTime?: string;
  createdDate?: string;
  description?: string;
  ownerName?: string;
};

type ActivityState = {
  isLoading: boolean;
  error: string;
  activities: ActivityRecord[];
};

const NYIA_1_STATUS_OPTIONS = ['Not Applicable', 'Needed', 'Scheduled', 'Done'];
const NYIA_FIELDS: NyiaField[] = ['nyia1Date', 'nyia1Status', 'nyia2Date', 'nyia2Status'];
const NYIA_2_STATUS_OPTIONS = [
  'Connect for UAS',
  'Done',
  'Medical Review',
  'NA - Agency Transfer',
  'Assigned',
  'NA - Untagged',
  'Needed',
  'Pending Acceptance',
  'Pending Recert',
  'POC Rejected',
  'POC Accepted',
  'Scheduled',
];
const NO_STATUS_LABEL = '(No status)';
const EASTERN_TIME_ZONE = 'America/New_York';
const EXPANDED_NOTE_ROW_HEIGHT = 164;
const QUICK_ENTRY_PAGE_SIZE = 50;

const easternDateTimeFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: EASTERN_TIME_ZONE,
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

const easternDateTimePartsFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: EASTERN_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

const formatCreatedDate = (value?: string) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString();
};

const formatDateOnly = (value?: string) => {
  if (!value) return '';
  const dateOnlyMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;
    return `${Number(month)}/${Number(day)}/${year}`;
  }
  return formatCreatedDate(value);
};

const formatDateTime = (value?: string) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return easternDateTimeFormatter.format(date);
};

const getDateTimeLocalValue = (value?: string) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const parts = Object.fromEntries(
    easternDateTimePartsFormatter.formatToParts(date)
      .filter((part) => part.type !== 'literal')
      .map((part) => [part.type, part.value]),
  );
  if (!parts.year || !parts.month || !parts.day || !parts.hour || !parts.minute) return '';
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
};

const getActivityTimestamp = (activity: ActivityRecord) =>
  formatDateTime(activity.activityDate) ||
  formatDateTime(activity.startDateTime) ||
  formatDateTime(activity.createdDate) ||
  'N/A';

const rowStringValue = (row: GridLandingRow, field: NyiaField | 'notes') =>
  String(row[field] || '').trim();

function SaveEditButton({
  rowId,
  field,
  isSaving,
  isMultiline = false,
  onSave,
}: {
  rowId: GridRowId;
  field: string;
  isSaving: boolean;
  isMultiline?: boolean;
  onSave: (rowId: GridRowId, field: string) => void;
}) {
  return (
    <IconButton
      aria-label="Save"
      size="small"
      disabled={isSaving}
      onMouseDown={(event) => event.preventDefault()}
      onClick={(event) => {
        event.stopPropagation();
        onSave(rowId, field);
      }}
      sx={{ p: 0.5, alignSelf: isMultiline ? 'flex-start' : 'stretch', mt: isMultiline ? 1 : 0 }}
    >
      {isSaving ? <CircularProgress size={16} /> : <CheckIcon fontSize="small" />}
    </IconButton>
  );
}

function NotesEditCell({
  params,
  isSaving,
  onSave,
}: {
  params: GridRenderEditCellParams<GridLandingRow, string>;
  isSaving: boolean;
  onSave: (rowId: GridRowId, field: string, value?: unknown) => void;
}) {
  const [value, setValue] = useState(String(params.value || ''));
  const didPlaceCursorRef = useRef(false);

  return (
    <Box display="flex" alignItems="flex-start" gap={0.75} width="100%" height="100%">
      <TextField
        autoFocus
        fullWidth
        multiline
        minRows={4}
        maxRows={5}
        value={value}
        variant="outlined"
        inputRef={(input) => {
          if (!input || didPlaceCursorRef.current) return;
          didPlaceCursorRef.current = true;
          window.requestAnimationFrame(() => {
            const end = value.length;
            input.setSelectionRange(end, end);
          });
        }}
        onChange={(event) => {
          const nextValue = event.target.value;
          setValue(nextValue);
          void params.api.setEditCellValue({
            id: params.id,
            field: params.field,
            value: nextValue,
          });
        }}
        sx={{
          py: 1,
          '& .MuiInputBase-root': { alignItems: 'flex-start', fontSize: '0.875rem', lineHeight: 1.4, p: 1 },
          '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
        }}
      />
      <SaveEditButton
        rowId={params.id}
        field={params.field}
        isSaving={isSaving}
        isMultiline
        onSave={() => {
          void params.api.setEditCellValue({ id: params.id, field: params.field, value });
          onSave(params.id, params.field, value);
        }}
      />
    </Box>
  );
}

function StatusBarChart({
  data,
  selectedStatus,
  onStatusClick,
}: {
  data: { status: string; label: string; value: number }[];
  selectedStatus: string | null;
  onStatusClick: (status: string) => void;
}) {
  const theme = useTheme();
  const max = Math.max(...data.map((item) => item.value), 1);

  if (!data.length) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" sx={{ height: 220 }}>
        <Typography color="text.secondary">No records to chart.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', height: 260, display: 'flex', alignItems: 'flex-end', gap: 1.5, px: 1, pt: 2, overflowX: 'auto' }}>
      {data.map((item) => {
        const isSelected = selectedStatus === item.status;
        const muted = selectedStatus !== null && !isSelected;
        return (
          <Box
            key={item.status || NO_STATUS_LABEL}
            component="button"
            type="button"
            onClick={() => onStatusClick(item.status)}
            sx={{
              border: 0,
              background: 'transparent',
              p: 0,
              flex: '1 0 140px',
              minWidth: 140,
              height: '100%',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
              alignItems: 'stretch',
              opacity: muted ? 0.45 : 1,
            }}
          >
            <Box
              sx={{
                height: `${Math.max(18, (item.value / max) * 170)}px`,
                width: '100%',
                maxWidth: 190,
                mx: 'auto',
                borderRadius: '4px 4px 0 0',
                background: `linear-gradient(180deg, ${alpha(theme.palette.primary.light, 0.95)}, ${theme.palette.primary.main})`,
                color: '#fff',
                fontWeight: 700,
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'center',
                pb: 0.5,
              }}
            >
              {item.value}
            </Box>
            <Typography variant="caption" sx={{ mt: 1, lineHeight: 1.2, minHeight: 36, color: 'text.secondary', textAlign: 'center' }}>
              {item.label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

export default function QuickEntryLanding() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const apiRef = useGridApiRef();
  const user = getStoredUser();
  const [activeTab, setActiveTab] = useState(0);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<string | null>(null);
  const [quickEntryUser, setQuickEntryUser] = useState(() => getStoredQuickEntryUser());
  const [patientRecords, setPatientRecords] = useState<LandingRecord[]>([]);
  const [caregiverRecords, setCaregiverRecords] = useState<LandingRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedRowIds, setExpandedRowIds] = useState<Set<GridRowId>>(new Set());
  const [expandedNoteRowIds, setExpandedNoteRowIds] = useState<Set<GridRowId>>(new Set());
  const [savingCellKey, setSavingCellKey] = useState('');
  const [activitiesByRecordId, setActivitiesByRecordId] = useState<Record<string, ActivityState>>({});
  const activitiesByRecordIdRef = useRef<Record<string, ActivityState>>({});
  const expandedRowIdList = useMemo(() => Array.from(expandedRowIds), [expandedRowIds]);
  const canClearQuickEntryUserFilter = !searchParams.has('user') && !searchParams.has('USER');
  const qeId = user?.username?.startsWith('qe_') ? user.username.slice(3) : '';
  const floridaPortalUrl = appendQuickEntryUserToUrl(
    qeId
      ? `https://portal.faithhealthcare.com/start?qe=true&id=${encodeURIComponent(qeId)}`
      : 'https://portal.faithhealthcare.com/start?qe=true',
    quickEntryUser,
  );
  const exportFileName = `${activeTab === 0 ? 'Patients' : 'Caregivers'}-${new Date().toISOString().slice(0, 10)}`;
  const exportOptions = useMemo(() => ({ fileName: exportFileName, utf8WithBom: true }), [exportFileName]);

  useEffect(() => {
    activitiesByRecordIdRef.current = activitiesByRecordId;
  }, [activitiesByRecordId]);

  useEffect(() => {
    const loadRecords = async () => {
      setIsLoading(true);
      setError('');
      try {
        const res = await authFetch('/api/quick-entry');
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.message || 'Unable to load quick entry records.');
        setPatientRecords(data.records || []);
        setCaregiverRecords(data.caregiverRecords || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unable to load quick entry records.');
      } finally {
        setIsLoading(false);
      }
    };
    void loadRecords();
  }, []);

  const expandNoteRow = (rowId: GridRowId) => {
    setExpandedNoteRowIds((prev) => new Set(prev).add(rowId));
    window.requestAnimationFrame(() => apiRef.current?.resetRowHeights());
  };

  const toggleNoteRow = (rowId: GridRowId) => {
    setExpandedNoteRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
    window.requestAnimationFrame(() => apiRef.current?.resetRowHeights());
  };

  const startEditableCell = (rowId: GridRowId, field: string) => {
    if (field === 'notes') expandNoteRow(rowId);
    window.requestAnimationFrame(() => {
      try {
        apiRef.current?.startCellEditMode({ id: rowId, field });
      } catch {
        // Grid can throw if the cell is already editing.
      }
    });
  };

  const stopEditableCell = (rowId: GridRowId, field: string) => {
    try {
      apiRef.current?.stopCellEditMode({ id: rowId, field });
    } catch {
      // Grid can throw if edit mode already stopped.
    }
  };

  const saveEditedCell = (rowId: GridRowId, field: string) => {
    setSavingCellKey(`${rowId}-${field}`);
    window.requestAnimationFrame(() => stopEditableCell(rowId, field));
  };

  const renderEditableDisplayCell = (params: EditableDisplayParams, isExpanded = false, canEdit = true) => (
    <Box display="flex" alignItems={isExpanded ? 'flex-start' : 'center'} gap={0.75} width="100%" height="100%">
      <Typography
        variant="body2"
        title={String(params.value || '')}
        sx={{
          flex: 1,
          py: isExpanded ? 1 : 0,
          whiteSpace: isExpanded ? 'pre-wrap' : 'nowrap',
          overflowWrap: isExpanded ? 'anywhere' : 'normal',
          overflowX: 'hidden',
          overflowY: isExpanded ? 'auto' : 'hidden',
          textOverflow: isExpanded ? 'clip' : 'ellipsis',
          lineHeight: 1.4,
          maxHeight: '100%',
          minWidth: 0,
          pr: isExpanded ? 0.5 : 0,
        }}
      >
        {String(params.value || '')}
      </Typography>
      {canEdit && (
        <IconButton
          aria-label={`Edit ${params.colDef.headerName || params.field}`}
          size="small"
          onClick={(event) => {
            event.stopPropagation();
            startEditableCell(params.id, params.field);
          }}
          sx={{ p: 0.25, flexShrink: 0, alignSelf: isExpanded ? 'flex-start' : 'center', mt: isExpanded ? 0.75 : 0 }}
        >
          <EditIcon fontSize="inherit" />
        </IconButton>
      )}
    </Box>
  );

  const renderSaveButton = (rowId: GridRowId, field: string, isMultiline = false) => (
    <SaveEditButton
      rowId={rowId}
      field={field}
      isSaving={savingCellKey === `${rowId}-${field}`}
      isMultiline={isMultiline}
      onSave={saveEditedCell}
    />
  );

  const renderNyiaDateEditCell = (params: GridRenderEditCellParams<GridLandingRow, string>) => (
    <Box display="flex" alignItems="stretch" gap={0.75} width="100%" height="100%">
      <TextField
        type="datetime-local"
        autoFocus
        fullWidth
        value={getDateTimeLocalValue(params.value)}
        onChange={(event) => {
          void params.api.setEditCellValue({ id: params.id, field: params.field, value: event.target.value });
        }}
        sx={{ '& .MuiInputBase-root': { height: '100%' } }}
      />
      {renderSaveButton(params.id, params.field)}
    </Box>
  );

  const renderSingleSelectEditCell = (params: GridRenderEditCellParams<GridLandingRow, string>, options: string[]) => (
    <Box display="flex" alignItems="stretch" gap={0.75} width="100%" height="100%">
      <TextField
        select
        autoFocus
        fullWidth
        value={params.value || ''}
        onChange={(event) => {
          void params.api.setEditCellValue({ id: params.id, field: params.field, value: event.target.value });
        }}
        sx={{ '& .MuiInputBase-root': { height: '100%' }, '& .MuiSelect-select': { display: 'flex', alignItems: 'center', pr: '44px !important' } }}
      >
        <MenuItem value=""><em>None</em></MenuItem>
        {options.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
      </TextField>
      {renderSaveButton(params.id, params.field)}
    </Box>
  );

  const patientColumns = useMemo<GridColDef<GridLandingRow>[]>(
    () => [
      { field: 'name', headerName: 'Name', flex: 1.4, minWidth: 220 },
      { field: 'mobile', headerName: 'Mobile', flex: 1, minWidth: 140 },
      { field: 'phone', headerName: 'Phone', flex: 1, minWidth: 140 },
      { field: 'status', headerName: 'Status', flex: 1, minWidth: 160 },
      { field: 'lineOfBusiness', headerName: 'Line of Business', flex: 1, minWidth: 180 },
      { field: 'startOfCareDate', headerName: 'Start of Care Date', flex: 1, minWidth: 170 },
      {
        field: 'hardToReach',
        headerName: 'Hard to Reach',
        flex: 0.8,
        minWidth: 130,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => <Checkbox checked={!!params.value} disabled size="small" />,
      },
      {
        field: 'nyia1Date',
        headerName: 'NYIA 1 Date',
        flex: 1.1,
        minWidth: 220,
        editable: true,
        renderCell: (params) => renderEditableDisplayCell({ ...params, value: formatDateTime(params.value as string) }),
        renderEditCell: renderNyiaDateEditCell,
      },
      {
        field: 'nyia1Status',
        headerName: 'NYIA 1 Status',
        flex: 1.2,
        minWidth: 220,
        editable: true,
        type: 'singleSelect',
        valueOptions: NYIA_1_STATUS_OPTIONS,
        renderCell: (params) => renderEditableDisplayCell(params),
        renderEditCell: (params) => renderSingleSelectEditCell(params, NYIA_1_STATUS_OPTIONS),
      },
      {
        field: 'nyia2Date',
        headerName: 'NYIA 2 Date',
        flex: 1.1,
        minWidth: 220,
        editable: true,
        renderCell: (params) => renderEditableDisplayCell({ ...params, value: formatDateTime(params.value as string) }),
        renderEditCell: renderNyiaDateEditCell,
      },
      {
        field: 'nyia2Status',
        headerName: 'NYIA 2 Status',
        flex: 1.35,
        minWidth: 260,
        editable: true,
        type: 'singleSelect',
        valueOptions: NYIA_2_STATUS_OPTIONS,
        renderCell: (params) => renderEditableDisplayCell(params),
        renderEditCell: (params) => renderSingleSelectEditCell(params, NYIA_2_STATUS_OPTIONS),
      },
      { field: 'medicaidPayer', headerName: 'Medicaid Payer', flex: 1.1, minWidth: 190 },
      { field: 'medicaidPlanCode', headerName: 'Medicaid Plan Code', flex: 1.1, minWidth: 210 },
      { field: 'medcheckMedicaidStatus', headerName: 'MedCheck Medicaid Status', flex: 1.1, minWidth: 220 },
      { field: 'createdBy', headerName: 'Created By', flex: 1, minWidth: 130 },
      { field: 'createdDate', headerName: 'Created Date', flex: 1, minWidth: 150 },
    ],
    [savingCellKey],
  );

  const caregiverColumns = useMemo<GridColDef<GridLandingRow>[]>(
    () => [
      { field: 'name', headerName: 'Name', flex: 1.4, minWidth: 220 },
      { field: 'mobile', headerName: 'Mobile', flex: 1, minWidth: 140 },
      { field: 'phone', headerName: 'Phone', flex: 1, minWidth: 140 },
      { field: 'email', headerName: 'Email', flex: 1.2, minWidth: 200 },
      { field: 'status', headerName: 'Status', flex: 1, minWidth: 160 },
      { field: 'primaryLanguage', headerName: 'Primary Language', flex: 1, minWidth: 160 },
      { field: 'createdBy', headerName: 'Created By', flex: 1, minWidth: 130 },
      { field: 'createdDate', headerName: 'Created Date', flex: 1, minWidth: 150 },
    ],
    [],
  );

  const notesColumn = useMemo<GridColDef<GridLandingRow>>(
    () => ({
      field: 'notes',
      headerName: 'Notes',
      flex: 1.4,
      minWidth: 260,
      editable: true,
      renderEditCell: (params) => (
        <NotesEditCell
          params={params}
          isSaving={savingCellKey === `${params.id}-${params.field}`}
          onSave={(rowId, field) => saveEditedCell(rowId, field)}
        />
      ),
      renderCell: (params) => renderEditableDisplayCell(params, expandedNoteRowIds.has(params.id)),
    }),
    [expandedNoteRowIds, savingCellKey],
  );

  const patientGridRows = useMemo<GridLandingRow[]>(
    () => patientRecords.map((record, index) => ({
      ...record,
      uiId: record.id || `Patient-${index}`,
      name: record.name || 'N/A',
      mobile: record.mobile || '',
      phone: record.phone || '',
      status: record.status || '',
      lineOfBusiness: record.lineOfBusiness || '',
      startOfCareDate: formatDateOnly(record.startOfCareDate),
      hardToReach: record.hardToReach ?? false,
      nyia1Date: record.nyia1Date || '',
      nyia1Status: record.nyia1Status || '',
      nyia2Date: record.nyia2Date || '',
      nyia2Status: record.nyia2Status || '',
      medicaidPayer: record.medicaidPayer || '',
      medicaidPlanCode: record.medicaidPlanCode || '',
      medcheckMedicaidStatus: record.medcheckMedicaidStatus || '',
      notes: record.notes || '',
      createdBy: record.createdBy || '',
      createdDate: formatCreatedDate(record.createdDate),
    })),
    [patientRecords],
  );

  const caregiverGridRows = useMemo<GridLandingRow[]>(
    () => caregiverRecords.map((record, index) => ({
      ...record,
      uiId: record.id || `Caregiver-${index}`,
      name: record.name || 'N/A',
      mobile: record.mobile || '',
      phone: record.phone || '',
      email: record.email || '',
      status: record.status || '',
      primaryLanguage: record.primaryLanguage || '',
      notes: record.notes || '',
      createdBy: record.createdBy || '',
      createdDate: formatCreatedDate(record.createdDate),
    })),
    [caregiverRecords],
  );

  const activeColumns = activeTab === 0 ? patientColumns : caregiverColumns;
  const activeColumnsWithNotes = useMemo(
    () => [...activeColumns.slice(0, -1), notesColumn, ...activeColumns.slice(-1)],
    [activeColumns, notesColumn],
  );
  const activeGridRows = activeTab === 0 ? patientGridRows : caregiverGridRows;
  const quickEntryUserGridRows = useMemo(
    () => activeGridRows.filter((row) => quickEntryUserMatches(row.createdBy, quickEntryUser)),
    [activeGridRows, quickEntryUser],
  );
  const statusChartData = useMemo(() => {
    const counts = new Map<string, number>();
    quickEntryUserGridRows.forEach((row) => {
      const status = row.status?.trim() || '';
      counts.set(status, (counts.get(status) || 0) + 1);
    });
    return Array.from(counts.entries())
      .map(([status, value]) => ({ status, value, label: status || NO_STATUS_LABEL }))
      .sort((a, b) => b.value - a.value || a.label.localeCompare(b.label));
  }, [quickEntryUserGridRows]);
  const filteredGridRows = useMemo(
    () => selectedStatusFilter === null
      ? quickEntryUserGridRows
      : quickEntryUserGridRows.filter((row) => (row.status?.trim() || '') === selectedStatusFilter),
    [quickEntryUserGridRows, selectedStatusFilter],
  );

  const fetchActivitiesForRecord = async (record: GridLandingRow) => {
    const rowKey = record.uiId;
    if (!record.id) return;
    const existing = activitiesByRecordIdRef.current[rowKey];
    if (existing?.isLoading || existing?.activities?.length || existing?.error) return;

    setActivitiesByRecordId((prev) => ({
      ...prev,
      [rowKey]: { isLoading: true, error: '', activities: [] },
    }));

    try {
      const res = await authFetch(`/api/quick-entry/${record.sourceType}/${record.id}/activities`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Unable to load activities.');
      setActivitiesByRecordId((prev) => ({
        ...prev,
        [rowKey]: { isLoading: false, error: '', activities: data.activities || [] },
      }));
    } catch (err) {
      setActivitiesByRecordId((prev) => ({
        ...prev,
        [rowKey]: { isLoading: false, error: err instanceof Error ? err.message : 'Unable to load activities.', activities: [] },
      }));
    }
  };

  const handleExpandForRow = (row: GridLandingRow) => {
    if (!row?.uiId) return;
    let isExpanding = false;
    setExpandedRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(row.uiId)) next.delete(row.uiId);
      else {
        next.add(row.uiId);
        isExpanding = true;
      }
      return next;
    });
    if (isExpanding) void fetchActivitiesForRecord(row);
  };

  const handleCellClick = (params: GridCellParams<GridLandingRow>, event?: { stopPropagation?: () => void }) => {
    if (params.field === '__detail_panel_toggle__') return;
    if (params.field === 'notes') {
      event?.stopPropagation?.();
      if (apiRef.current?.getCellMode(params.id, 'notes') === 'edit') return;
      toggleNoteRow(params.id);
      return;
    }
    handleExpandForRow(params.row as GridLandingRow);
  };

  const handleProcessRowUpdate = async (updatedRow: GridLandingRow, originalRow: GridLandingRow) => {
    const changedNyiaFields = NYIA_FIELDS.filter(
      (field) => rowStringValue(updatedRow, field) !== rowStringValue(originalRow, field),
    );
    const notesChanged = rowStringValue(updatedRow, 'notes') !== rowStringValue(originalRow, 'notes');

    if (!changedNyiaFields.length && !notesChanged) {
      setSavingCellKey('');
      return originalRow;
    }

    const commitUpdatedRow = (row: GridLandingRow) => {
      const updateRecord = (record: LandingRecord) =>
        record.id === row.id && record.sourceType === row.sourceType
          ? { ...record, ...row }
          : record;
      if (row.sourceType === 'Patient') setPatientRecords((prev) => prev.map(updateRecord));
      else setCaregiverRecords((prev) => prev.map(updateRecord));
    };

    try {
      if (notesChanged) {
        setSavingCellKey(`${updatedRow.uiId}-notes`);
        const res = await authFetch(`/api/quick-entry/${updatedRow.sourceType}/${updatedRow.id}/notes`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ medallionNotes: String(updatedRow.notes || '').trim() }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.message || 'Unable to update notes.');
        updatedRow = { ...updatedRow, notes: data.notes || '' };
        commitUpdatedRow(updatedRow);
      }

      if (changedNyiaFields.length && updatedRow.sourceType === 'Patient') {
        setSavingCellKey(`${updatedRow.uiId}-${changedNyiaFields[0]}`);
        const body = changedNyiaFields.reduce<Record<string, unknown>>((acc, field) => {
          acc[field] = updatedRow[field] || '';
          return acc;
        }, {});
        const res = await authFetch(`/api/quick-entry/Patient/${updatedRow.id}/nyia`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.message || 'Unable to update NYIA fields.');
        updatedRow = {
          ...updatedRow,
          nyia1Date: data.nyia1Date ?? updatedRow.nyia1Date,
          nyia1Status: data.nyia1Status ?? updatedRow.nyia1Status,
          nyia2Date: data.nyia2Date ?? updatedRow.nyia2Date,
          nyia2Status: data.nyia2Status ?? updatedRow.nyia2Status,
        };
        commitUpdatedRow(updatedRow);
      }
    } finally {
      setSavingCellKey('');
    }

    return updatedRow;
  };

  const handleExpandedRowIdsChange = (ids: GridRowId[] | Set<GridRowId>) => {
    const nextIds = ids instanceof Set ? ids : new Set(ids);
    const newlyExpandedIds = Array.from(nextIds).filter((id) => !expandedRowIds.has(id));
    setExpandedRowIds(new Set(nextIds));
    newlyExpandedIds.forEach((id) => {
      const row = filteredGridRows.find((item) => item.uiId === id);
      if (row) void fetchActivitiesForRecord(row);
    });
  };

  return (
    <PageScaffold
      title="Quick Entry Leads"
      subtitle="Records referred by this quick-entry organization."
      maxWidth="xl"
        actions={(
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button variant="outlined" sx={{ textTransform: 'none' }} onClick={() => window.open(floridaPortalUrl, '_blank', 'noopener,noreferrer')}>
            Switch to Florida
          </Button>
          <Button variant="contained" sx={{ textTransform: 'none' }} onClick={() => navigate(appendQuickEntryUserToUrl('/quick-entry', quickEntryUser))}>
            New Lead
          </Button>
        </Stack>
      )}
    >
        <Tabs
          value={activeTab}
          onChange={(_event, value) => {
            setActiveTab(value);
            setSelectedStatusFilter(null);
            setExpandedRowIds(new Set());
            setExpandedNoteRowIds(new Set());
          }}
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Patients" sx={{ textTransform: 'none' }} />
          <Tab label="Caregivers" sx={{ textTransform: 'none' }} />
        </Tabs>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={6}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={2.5}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} gap={2}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700}>
                    {activeTab === 0 ? 'Patient Status' : 'Caregiver Status'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Click a status to filter the grid.</Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                  {selectedStatusFilter !== null && (
                    <Chip
                      label={`Filtered by ${selectedStatusFilter || NO_STATUS_LABEL}`}
                      onDelete={() => {
                        setSelectedStatusFilter(null);
                        setExpandedRowIds(new Set());
                      }}
                      color="primary"
                      variant="outlined"
                    />
                  )}
                  {quickEntryUser && (
                    <Chip
                      label={`Created by ${quickEntryUser}`}
                      onDelete={canClearQuickEntryUserFilter ? () => {
                        clearStoredQuickEntryUser();
                        setQuickEntryUser('');
                        setSelectedStatusFilter(null);
                        setExpandedRowIds(new Set());
                      } : undefined}
                      color="secondary"
                      variant="outlined"
                    />
                  )}
                </Stack>
              </Box>
              <StatusBarChart
                data={statusChartData}
                selectedStatus={selectedStatusFilter}
                onStatusClick={(status) => {
                  setSelectedStatusFilter((previous) => (previous === status ? null : status));
                  setExpandedRowIds(new Set());
                }}
              />
            </Paper>

            <Box sx={{ height: 540, width: '100%' }}>
              <DataGridPremium
                key={activeTab}
                apiRef={apiRef}
                rows={filteredGridRows}
                getRowId={(row) => (row as GridLandingRow).uiId}
                columns={activeColumnsWithNotes}
                getRowHeight={(params) => (expandedNoteRowIds.has(params.id) ? EXPANDED_NOTE_ROW_HEIGHT : null)}
                sx={{
                  ...standardDataGridSx,
                }}
                density="compact"
                pagination
                disableVirtualization
                disableColumnMenu
                disableColumnSelector
                disableRowSelectionOnClick
                onCellClick={handleCellClick}
                onCellDoubleClick={(params) => {
                  if (params.field === 'notes') expandNoteRow(params.id);
                }}
                onCellEditStart={(params) => {
                  if (params.field === 'notes') expandNoteRow(params.id);
                }}
                processRowUpdate={handleProcessRowUpdate}
                onProcessRowUpdateError={(err) => setError(err instanceof Error ? err.message : 'Unable to update record.')}
                detailPanelExpandedRowIds={expandedRowIdList}
                onDetailPanelExpandedRowIdsChange={handleExpandedRowIdsChange}
                getDetailPanelHeight={() => 'auto'}
                getDetailPanelContent={({ row }) => {
                  const rowData = row as GridLandingRow;
                  const state = activitiesByRecordId[rowData.uiId];
                  const hasLoaded = Boolean(state && !state.isLoading && !state.error);
                  return (
                    <Box sx={{ px: 3, py: 2, backgroundColor: 'grey.50' }}>
                      {!state || state.isLoading ? (
                        <Box display="flex" alignItems="center" gap={1}>
                          <CircularProgress size={18} />
                          <Typography variant="body2" color="text.secondary">Loading activities...</Typography>
                        </Box>
                      ) : state.error ? (
                        <Typography variant="body2" color="error">{state.error}</Typography>
                      ) : hasLoaded && state.activities.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">No activities found.</Typography>
                      ) : (
                        <Stack spacing={1.25}>
                          {(state.activities || []).map((activity, index, activities) => (
                            <Box key={activity.id}>
                              <Typography variant="body2" fontWeight={600}>{activity.subject || '(No subject)'}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                <Box component="span" sx={{ fontWeight: 700 }}>{getActivityTimestamp(activity)}</Box>
                                {activity.status ? ` - ${activity.status}` : ''}
                                {activity.ownerName ? ` - ${activity.ownerName}` : ''}
                              </Typography>
                              {activity.description && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{activity.description}</Typography>}
                              {index < activities.length - 1 ? <Divider sx={{ mt: 1 }} /> : null}
                            </Box>
                          ))}
                        </Stack>
                      )}
                    </Box>
                  );
                }}
                pageSizeOptions={[25, 50, 100]}
                initialState={{ pagination: { paginationModel: { pageSize: QUICK_ENTRY_PAGE_SIZE } } }}
                slots={{
                  toolbar: () => (
                    <GridToolbarContainer sx={{ px: 1.5, py: 1, justifyContent: 'flex-end', gap: 1, borderBottom: 1, borderColor: 'divider', backgroundColor: 'grey.50' }}>
                      <GridToolbarFilterButton />
                      <GridToolbarExport csvOptions={exportOptions} excelOptions={exportOptions} />
                    </GridToolbarContainer>
                  ),
                  noRowsOverlay: () => (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                      <Typography color="text.secondary">No records found.</Typography>
                    </Box>
                  ),
                }}
              />
            </Box>
          </Stack>
        )}
    </PageScaffold>
  );
}
